import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const LOGO_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_aac32766-5fa5-4a83-81c9-03bafeaebc9e/artifacts/bi1rz20k_loyerbook.png';

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl sm:text-2xl font-bold text-[#0D1B2A]">{title}</h2>
      <div className="mt-3 text-sm text-slate-700 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function CGU() {
  return (
    <div data-testid="page-cgu" className="min-h-screen bg-white text-[#0D1B2A]">
      <header className="max-w-4xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0D1B2A]">
          <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
        </Link>
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="LoyerBook" className="h-10 w-10" />
          <div className="leading-tight">
            <div className="text-lg font-extrabold">LoyerBook</div>
            <div className="text-[10px] text-slate-500">Le carnet de loyer numérique</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 pb-16">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#E6F1FF] border border-[#BEDBFF] px-3 py-1 text-xs font-semibold text-[#1677FF]">
          <FileText className="w-3 h-3" /> Version en vigueur — {new Date().toLocaleDateString('fr-FR')}
        </div>
        <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight">Conditions générales d&apos;utilisation</h1>
        <p className="mt-3 text-sm text-slate-600">
          Les présentes conditions régissent l&apos;utilisation de la plateforme LoyerBook par tout utilisateur
          (bailleur, locataire, agent, administrateur).
        </p>

        <Section title="1. Objet">
          <p>LoyerBook est un service SaaS de gestion numérique des loyers. Il permet aux bailleurs d&apos;enregistrer
          leurs locataires, leurs paiements, et de générer des reçus officiels.</p>
        </Section>

        <Section title="2. Accès et création de compte">
          <p>L&apos;accès à LoyerBook est fermé à l&apos;auto-inscription. Les comptes sont créés :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Par un <strong>agent LoyerBook</strong> ou par le <strong>Platform Owner</strong> pour les bailleurs.</li>
            <li>Par un <strong>bailleur</strong> pour ses propres locataires.</li>
          </ul>
          <p>Toute création de compte requiert le consentement explicite de la personne concernée.</p>
        </Section>

        <Section title="3. Abonnement">
          <p>L&apos;utilisation de LoyerBook est soumise à un abonnement annuel selon les offres suivantes :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Essai gratuit — 2 locataires, 30 jours.</li>
            <li>Standard — 5 locataires — 10 USD/an.</li>
            <li>Pro — 10 locataires — 20 USD/an.</li>
            <li>Business — 20 locataires — 35 USD/an.</li>
            <li>Enterprise — illimité — sur devis.</li>
          </ul>
        </Section>

        <Section title="4. Obligations de l&apos;utilisateur">
          <ul className="list-disc pl-6 space-y-1">
            <li>Fournir des informations exactes et à jour.</li>
            <li>Conserver la confidentialité de ses identifiants.</li>
            <li>Ne pas tenter de contourner les mesures de sécurité.</li>
            <li>Respecter la législation applicable dans son pays.</li>
          </ul>
        </Section>

        <Section title="5. Rôle de LoyerBook">
          <p>LoyerBook fournit un outil d&apos;enregistrement et de suivi. Nous n&apos;intervenons pas dans
          la relation contractuelle entre bailleur et locataire, ni dans le flux financier réel des loyers.</p>
        </Section>

        <Section title="6. Suspension et suppression de compte">
          <p>Le Platform Owner peut suspendre ou supprimer un compte en cas de :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Non-paiement d&apos;abonnement à l&apos;échéance.</li>
            <li>Violation des présentes conditions.</li>
            <li>Activité frauduleuse ou abusive.</li>
          </ul>
        </Section>

        <Section title="7. Responsabilité">
          <p>LoyerBook s&apos;engage à une obligation de moyens sur la disponibilité du service (objectif 99,9%).
          Notre responsabilité est limitée au montant de l&apos;abonnement annuel.</p>
        </Section>

        <Section title="8. Propriété intellectuelle">
          <p>La marque, le logo, le code source et l&apos;interface de LoyerBook sont notre propriété exclusive.</p>
        </Section>

        <Section title="9. Loi applicable">
          <p>Les présentes conditions sont régies par le droit du pays d&apos;utilisation. En cas de litige,
          les parties recherchent d&apos;abord une solution amiable.</p>
        </Section>

        <Section title="10. Contact">
          <p>Pour toute question : <a href="mailto:aide@loyerbook.com" className="text-[#1677FF] underline">aide@loyerbook.com</a>.</p>
        </Section>
      </main>
    </div>
  );
}
